import React, { useMemo } from 'react';
import {
  Grid,
  Typography, useTheme,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { CustomerDetail } from '../../models/customer';
import { CustomerInfoSkeleton } from './CustomerInfoSkeleton';
import { IAccountReduxState, IAppReduxState } from '../../models';

export interface CustomerDetailProp {
  customerDetail: CustomerDetail | undefined;
  selectedMeterId: string;
  isLoading: boolean;
}

/**
 * Function to render customer Account information
 * @returns {JSX.Element}
 */
function CustomerInformation({ customerDetail, selectedMeterId, isLoading }: CustomerDetailProp) {
  const theme = useTheme();

  const account = useSelector<IAppReduxState, IAccountReduxState>((x) => x.account);

  const selectedServicePoint = useMemo(
    () => customerDetail?.servicePoints.find((sp) => sp.meterNumber === selectedMeterId),
    [customerDetail?.servicePoints, selectedMeterId],
  );

  const mappedServiceAddress = useMemo(() => {
    const serviceAdress = selectedServicePoint?.seviceAddress;
    return serviceAdress
      ? (
        <>
          <Typography>{serviceAdress?.address}</Typography>
          <Typography>{`${serviceAdress?.city}, ${customerDetail?.stateCode}`}</Typography>
          <Typography>{serviceAdress?.postalCode}</Typography>
        </>
      ) : '';
  }, [customerDetail?.stateCode, selectedServicePoint?.seviceAddress]);

  return (
    <Grid container sx={{ wordWrap: 'break-word' }}>
      <Grid item xs={8}>
        <Typography variant="body1" color={theme.palette.bluePlan.contrastText}>
          <FormattedMessage id="rate-plan-account-number" />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <CustomerInfoSkeleton isLoading={isLoading} customerDetail={customerDetail}>
          <Typography variant="body1" color={theme.palette.altBackground.contrastText}>
            {account.accountNumber}
          </Typography>
        </CustomerInfoSkeleton>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body1" color={theme.palette.bluePlan.contrastText}>
          <FormattedMessage id="rate-plan-name" />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <CustomerInfoSkeleton isLoading={isLoading} customerDetail={customerDetail}>
          <Typography variant="body1" color={theme.palette.altBackground.contrastText}>
            {customerDetail?.customerName}
          </Typography>
        </CustomerInfoSkeleton>
      </Grid>
      <Grid item xs={8} mt={1}>
        <Typography variant="body1" color={theme.palette.bluePlan.contrastText}>
          <FormattedMessage id="rate-plan-service-address" />
        </Typography>
      </Grid>
      <Grid item xs={4} mt={1}>
        <CustomerInfoSkeleton isLoading={isLoading} customerDetail={customerDetail}>
          <Typography variant="body1" component="span" color={theme.palette.altBackground.contrastText}>
            {mappedServiceAddress}
          </Typography>
        </CustomerInfoSkeleton>
      </Grid>
    </Grid>
  );
}

export { CustomerInformation };
